import React from 'react';
import { Skeleton } from '@/components/ui/skeleton';
import { asArray } from './helpers';

/**
 * The engagement endpoint returns an ARRAY of per-type rows —
 * `{ engtid, count_total, count_breakdown[], score_avg, engagement_type{...} }`
 * — not a flat metrics object. Render each type as a tile using its friendly
 * name as the label. A flat `{ key: number }` object is still handled as a
 * legacy fallback so older payloads don't regress.
 */
export const EngagementView: React.FC<{ loading: boolean; data: any }> = ({ loading, data }) => {
  if (loading) return <Skeleton className="h-24 w-full" />;

  const rows = asArray<any>(data);
  if (rows.length > 0) {
    const typeLabel = (r: any): string =>
      r?.engagement_type?.name ||
      r?.engagement_type?.name_system ||
      `Type #${r?.engtid ?? '—'}`;

    return (
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
        {rows.map((r: any) => {
          const pct = r?.count_breakdown?.[0]?.percentage;
          const avg = Number(r?.score_avg) || 0;
          return (
            <div
              key={r?.id ?? r?.engtid ?? typeLabel(r)}
              className="rounded-xl border border-gray-100 bg-gradient-to-br from-gray-50 to-white p-4"
              title={r?.engagement_type?.description || undefined}
            >
              <p className="text-2xl font-bold text-gray-900">
                {typeof r?.count_total === 'number' ? r.count_total.toLocaleString() : '—'}
              </p>
              <p className="mt-1 text-xs font-medium text-gray-700">{typeLabel(r)}</p>
              {(typeof pct === 'number' || avg > 0) && (
                <div className="mt-1 flex flex-wrap gap-x-2 text-[11px] text-gray-400">
                  {typeof pct === 'number' && <span>{pct}% of total</span>}
                  {avg > 0 && <span>avg score {avg.toFixed(1)}</span>}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  }

  // Legacy fallback: a flat { key: number } metrics object.
  const entries = data && typeof data === 'object' && !Array.isArray(data) ? Object.entries(data) : [];
  if (entries.length === 0) return <p className="py-8 text-center text-sm text-gray-400">No engagement data.</p>;
  return (
    <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
      {entries.map(([k, v]) => (
        <div key={k} className="rounded-xl border border-gray-100 bg-gradient-to-br from-gray-50 to-white p-4">
          <p className="text-2xl font-bold text-gray-900">
            {typeof v === 'number' ? v.toLocaleString() : String(v)}
          </p>
          <p className="mt-1 text-xs capitalize text-gray-500">{k.replace(/_/g, ' ')}</p>
        </div>
      ))}
    </div>
  );
};
